import { toZonedTime, fromZonedTime } from 'date-fns-tz';
import { parseISO, startOfDay, endOfDay, startOfWeek, endOfWeek, startOfMonth, endOfMonth } from 'date-fns';
/**
 * Convert UTC ISO string to user's timezone Date object
 * @param utcIsoString - UTC ISO string from database
 * @param userTimezone - User's IANA timezone (e.g., 'America/Los_Angeles')
 * @returns Date object representing time in user's timezone
 */
export function utcToUserTimezone(utcIsoString, userTimezone) {
    if (!utcIsoString)
        return new Date();
    try {
        const utcDate = parseISO(utcIsoString);
        return toZonedTime(utcDate, userTimezone);
    }
    catch (e) {
        console.error('Error converting UTC to user timezone:', e);
        return new Date();
    }
}
/**
 * Convert user timezone Date to UTC ISO string for storage
 * @param date - Date in user's timezone
 * @param userTimezone - User's IANA timezone
 * @returns UTC ISO string for database storage
 */
export function userTimezoneToUtc(date, userTimezone) {
    if (!date)
        return new Date().toISOString();
    try {
        return fromZonedTime(date, userTimezone).toISOString();
    }
    catch (e) {
        console.error('Error converting user timezone to UTC:', e);
        return new Date().toISOString();
    }
}
/**
 * Format UTC ISO string as datetime-local input value (YYYY-MM-DDTHH:mm)
 * @param utcIsoString - UTC ISO string from database
 * @param userTimezone - User's timezone
 * @returns String formatted for datetime-local input
 */
export function utcToDatetimeLocal(utcIsoString, userTimezone) {
    if (!utcIsoString)
        return '';
    try {
        const zonedDate = utcToUserTimezone(utcIsoString, userTimezone);
        const year = zonedDate.getFullYear();
        const month = String(zonedDate.getMonth() + 1).padStart(2, '0');
        const day = String(zonedDate.getDate()).padStart(2, '0');
        const hours = String(zonedDate.getHours()).padStart(2, '0');
        const minutes = String(zonedDate.getMinutes()).padStart(2, '0');
        return `${year}-${month}-${day}T${hours}:${minutes}`;
    }
    catch (e) {
        console.error('Error formatting for datetime-local:', e);
        return '';
    }
}
/**
 * Parse datetime-local input value to UTC ISO string
 * @param datetimeLocalValue - Value from datetime-local input (YYYY-MM-DDTHH:mm)
 * @param userTimezone - User's timezone
 * @returns UTC ISO string for storage
 */
export function datetimeLocalToUtc(datetimeLocalValue, userTimezone) {
    if (!datetimeLocalValue)
        return new Date().toISOString();
    try {
        // datetime-local gives us a string like "2024-01-15T14:30"
        // Parse it as if it's in the user's timezone
        const [datePart, timePart] = datetimeLocalValue.split('T');
        const [year, month, day] = datePart.split('-').map(Number);
        const [hours, minutes] = timePart.split(':').map(Number);
        const localDate = new Date(year, month - 1, day, hours, minutes, 0, 0);
        return userTimezoneToUtc(localDate, userTimezone);
    }
    catch (e) {
        console.error('Error parsing datetime-local:', e);
        return new Date().toISOString();
    }
}
/**
 * Get day boundaries in user's timezone, converted to UTC for database queries
 * @param date - The date to get boundaries for
 * @param userTimezone - User's timezone
 * @returns Object with UTC start and end of day
 */
export function getDayBoundariesUtc(date, userTimezone) {
    try {
        // Get start/end of day in user's timezone
        const zonedDate = toZonedTime(date, userTimezone);
        const dayStart = startOfDay(zonedDate);
        const dayEnd = endOfDay(zonedDate);
        // Convert back to UTC for database queries
        return {
            start: fromZonedTime(dayStart, userTimezone),
            end: fromZonedTime(dayEnd, userTimezone)
        };
    }
    catch (e) {
        console.error('Error getting day boundaries:', e);
        return {
            start: startOfDay(date),
            end: endOfDay(date)
        };
    }
}
/**
 * Get week boundaries in user's timezone, converted to UTC
 * @param date - Date within the week
 * @param userTimezone - User's timezone
 * @returns Object with UTC start and end of week
 */
export function getWeekBoundariesUtc(date, userTimezone) {
    try {
        const zonedDate = toZonedTime(date, userTimezone);
        const weekStart = startOfWeek(zonedDate, { weekStartsOn: 0 }); // Sunday
        const weekEnd = endOfWeek(zonedDate, { weekStartsOn: 0 });
        return {
            start: fromZonedTime(weekStart, userTimezone),
            end: fromZonedTime(weekEnd, userTimezone)
        };
    }
    catch (e) {
        console.error('Error getting week boundaries:', e);
        return {
            start: startOfWeek(date, { weekStartsOn: 0 }),
            end: endOfWeek(date, { weekStartsOn: 0 })
        };
    }
}
/**
 * Get month boundaries in user's timezone, converted to UTC
 * @param date - Date within the month
 * @param userTimezone - User's timezone
 * @returns Object with UTC start and end of month
 */
export function getMonthBoundariesUtc(date, userTimezone) {
    try {
        const zonedDate = toZonedTime(date, userTimezone);
        const monthStart = startOfMonth(zonedDate);
        const monthEnd = endOfMonth(zonedDate);
        return {
            start: fromZonedTime(monthStart, userTimezone),
            end: fromZonedTime(monthEnd, userTimezone)
        };
    }
    catch (e) {
        console.error('Error getting month boundaries:', e);
        return {
            start: startOfMonth(date),
            end: endOfMonth(date)
        };
    }
}
/**
 * Format time for display in user's timezone
 * @param utcIsoString - UTC ISO string from database
 * @param userTimezone - User's timezone
 * @returns Formatted time string (e.g., "2:30 PM")
 */
export function formatTimeInTimezone(utcIsoString, userTimezone) {
    if (!utcIsoString)
        return '';
    try {
        const utcDate = typeof utcIsoString === 'string' ? parseISO(utcIsoString) : utcIsoString;
        const zonedDate = toZonedTime(utcDate, userTimezone);
        return zonedDate.toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true,
            timeZone: userTimezone
        });
    }
    catch (e) {
        console.error('Error formatting time:', e);
        return '';
    }
}
/**
 * Format date and time for display in user's timezone
 * @param utcIsoString - UTC ISO string
 * @param userTimezone - User's timezone
 * @returns Formatted string (e.g., "Jan 15, 2:30 PM")
 */
export function formatDateTimeInTimezone(utcIsoString, userTimezone) {
    if (!utcIsoString)
        return '';
    try {
        const utcDate = typeof utcIsoString === 'string' ? parseISO(utcIsoString) : utcIsoString;
        const zonedDate = toZonedTime(utcDate, userTimezone);
        const dateStr = zonedDate.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            timeZone: userTimezone
        });
        const timeStr = zonedDate.toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true,
            timeZone: userTimezone
        });
        return `${dateStr}, ${timeStr}`;
    }
    catch (e) {
        console.error('Error formatting date time:', e);
        return '';
    }
}
/**
 * Extract time components from UTC ISO string in user's timezone
 * Used for recurring event generation
 * @param utcIsoString - UTC ISO string
 * @param userTimezone - User's timezone
 * @returns Object with hours, minutes, seconds
 */
export function getTimeComponentsInTimezone(utcIsoString, userTimezone) {
    try {
        const zonedDate = utcToUserTimezone(utcIsoString, userTimezone);
        return {
            hours: zonedDate.getHours(),
            minutes: zonedDate.getMinutes(),
            seconds: zonedDate.getSeconds()
        };
    }
    catch (e) {
        console.error('Error getting time components:', e);
        return { hours: 0, minutes: 0, seconds: 0 };
    }
}
/**
 * Create a date at specific time in user's timezone, return as UTC ISO string
 * @param date - Base date (in user's timezone)
 * @param hours - Hours (0-23)
 * @param minutes - Minutes (0-59)
 * @param userTimezone - User's timezone
 * @returns UTC ISO string
 */
export function createDateAtTimeInTimezone(date, hours, minutes, userTimezone) {
    try {
        const year = date.getFullYear();
        const month = date.getMonth();
        const day = date.getDate();
        const localDate = new Date(year, month, day, hours, minutes, 0, 0);
        return userTimezoneToUtc(localDate, userTimezone);
    }
    catch (e) {
        console.error('Error creating date at time:', e);
        return new Date().toISOString();
    }
}
/**
 * Get current time in user's timezone as ISO string
 * @param userTimezone - User's timezone
 * @returns UTC ISO string representing now in user's timezone
 */
export function getNowInTimezone(userTimezone) {
    try {
        const now = new Date();
        const zonedNow = toZonedTime(now, userTimezone);
        return fromZonedTime(zonedNow, userTimezone).toISOString();
    }
    catch (e) {
        console.error('Error getting now in timezone:', e);
        return new Date().toISOString();
    }
}
/**
 * Detect browser's timezone using Intl API
 * @returns IANA timezone string (e.g., 'America/Los_Angeles')
 */
export function detectBrowserTimezone() {
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone;
    }
    catch (e) {
        console.error('Error detecting browser timezone:', e);
        return 'America/Los_Angeles'; // Default fallback
    }
}
/**
 * Common timezones for dropdown selector
 */
export const COMMON_TIMEZONES = [
    { label: 'Pacific Time (PT)', value: 'America/Los_Angeles' },
    { label: 'Mountain Time (MT)', value: 'America/Denver' },
    { label: 'Central Time (CT)', value: 'America/Chicago' },
    { label: 'Eastern Time (ET)', value: 'America/New_York' },
    { label: 'Alaskan Time (AKT)', value: 'America/Anchorage' },
    { label: 'Hawaii Time (HT)', value: 'Pacific/Honolulu' },
    { label: 'London (GMT/BST)', value: 'Europe/London' },
    { label: 'Paris (CET/CEST)', value: 'Europe/Paris' },
    { label: 'Dubai (GST)', value: 'Asia/Dubai' },
    { label: 'India (IST)', value: 'Asia/Kolkata' },
    { label: 'Bangkok (ICT)', value: 'Asia/Bangkok' },
    { label: 'Shanghai (CST)', value: 'Asia/Shanghai' },
    { label: 'Tokyo (JST)', value: 'Asia/Tokyo' },
    { label: 'Sydney (AEDT/AEST)', value: 'Australia/Sydney' },
    { label: 'Auckland (NZDT/NZST)', value: 'Pacific/Auckland' }
];
